/**
 * The "what to advertise" subject model for the video-ad creator.
 *
 * A video ad can advertise one of three things; the chosen category drives what the "select what to
 * promote" picker shows and how the storyboard key visual + copy are seeded. Everything downstream
 * normalises to {@link VideoSubject}, so the three sources (banner pool, game data sources, gamification
 * features) sit behind adapters instead of the composer being banner-only. See
 * `.claude/specs/2026-09-23-video-ad-subjects.md`.
 */
import { defaultAdCta, type AdLanguageId } from './ad-language';
import type { Translate } from './video-composer';

/** What a video ad advertises. `promotion` is the original (banner-pool) path. */
export type VideoSubjectType = 'promotion' | 'game' | 'gamification';

/** The categories in display order (also the card order in the chooser). */
export const VIDEO_SUBJECT_TYPES: readonly VideoSubjectType[] = ['promotion', 'game', 'gamification'];

/**
 * A normalised subject the composer/storyboard consume, whatever its source. `id` is the source id
 * (banner_id / game id / feature id); `image_url` is the key visual; `destinationUrl` is where the
 * tracking link points (promotions carry their banner's cta_href, games/gamification fall back to the
 * brand homepage).
 */
export interface VideoSubject {
  subject_type: VideoSubjectType;
  id: string;
  image_url: string;
  title: string;
  subtitle?: string;
  ctaText?: string;
  destinationUrl?: string;
}

/** The category-card illustrations. PNG ONLY: the app server cannot serve webp (past incident). */
export const SUBJECT_ART: Record<VideoSubjectType, string> = {
  promotion: '/assets/video-ads/promotions.png',
  game: '/assets/video-ads/games.png',
  gamification: '/assets/video-ads/gamification.png',
};

/** Narrowing guard for a persisted/URL-sourced value. */
export const isVideoSubjectType = (value: unknown): value is VideoSubjectType =>
  value === 'promotion' || value === 'game' || value === 'gamification';

/**
 * The default on-screen call-to-action for a subject that carries no on-site CTA copy of its own. Games and
 * gamification features have no banner cta_text (a promotion does), so a composed storyboard would otherwise
 * end on an empty CTA; this supplies a generic one so the closing beat always reads as a real call to action.
 * The compose LLM is already instructed to end on a CTA beat using this text (spark buildRefinePrompt), so
 * filling it is the whole fix. Empty for promotions (they bring their own cta_text). An ad with a language gets the
 * subject type's default in that language (AD CONTENT, never through i18n); an older ad without one keeps the
 * owner's-language default it always had.
 */
export const subjectCta = (type: VideoSubjectType, t: Translate, language?: AdLanguageId): string => {
  if (type === 'promotion') return '';
  if ((language ?? '').trim()) return defaultAdCta(type, language);
  return type === 'game' ? t('Play now') : t('Join now');
};

/** Whether an ad's subject type is known well enough to mount its composer. A NEW ad's picked subject is; so is a
 *  reopened ad with no saved subjects (nothing to classify). A reopened ad WITH saved subjects is classified by
 *  matching them against the banner pool AND the gamification feature set, so it is only settled once both have
 *  loaded: mounted earlier, a Game or Gamification ad reads as a promotion ad and the composer prunes its subjects
 *  and re-saves it as one. */
export const reopenSubjectSettled = (s: {
  explicitSubject: boolean;
  savedSubjectCount: number;
  itemsLoaded: boolean;
  featuresLoading: boolean;
}): boolean => s.explicitSubject || s.savedSubjectCount === 0 || (s.itemsLoaded && !s.featuresLoading);
